import React, { useEffect, useState } from 'react';
import { Fingerprint, Building2, Hospital, Stethoscope, Info, RefreshCw, ShieldCheck, Database } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';
import DataFreshnessStrip from '../components/DataFreshnessStrip';
import GlobalEntitySearch from '../components/GlobalEntitySearch';
import { api } from '../api';

const ENTITY_META = {
  facility:  { Icon: Building2,   label: 'Skilled Nursing Facilities', color: '#4A7C5E', bg: '#E5F0EA' },
  hospital:  { Icon: Hospital,    label: 'Hospitals',                  color: '#34538F', bg: '#E2E9F5' },
  physician: { Icon: Stethoscope, label: 'Physicians',                 color: '#A06628', bg: '#FBE6CC' },
};

function fmtInt(n) {
  if (n == null) return '—';
  return Number(n).toLocaleString();
}

function CoverageCard({ entityType, dash }) {
  const meta = ENTITY_META[entityType];
  const Icon = meta.Icon;
  const total = dash?.totals?.[entityType] || 0;
  const matched = dash?.matched?.[entityType] || 0;
  const notFound = dash?.not_found?.[entityType] || 0;
  const pct = dash?.coverage_pct?.[entityType] || 0;
  return (
    <div className="rounded-xl bg-white border border-[#E5DCC4] p-5" data-testid={`entity-coverage-${entityType}`}>
      <div className="flex items-center gap-2.5 mb-3">
        <span className="p-1.5 rounded-md" style={{ background: meta.bg }}>
          <Icon size={16} style={{ color: meta.color }} />
        </span>
        <h3 className="font-display text-base font-semibold text-[#1A2622]">{meta.label}</h3>
      </div>
      <div className="mt-1">
        <div className="font-display text-4xl tracking-tight text-[#1A2622]" data-testid={`entity-coverage-pct-${entityType}`}>
          {pct.toFixed(1)}%
        </div>
        <div className="text-[10.5px] uppercase tracking-wider text-[#7A857F] mt-1">NPPES coverage</div>
      </div>
      <div className="mt-3 h-2 w-full rounded-full overflow-hidden bg-[#F0E7D2]">
        <div className="h-full rounded-full" style={{ width: `${Math.min(pct, 100)}%`, background: meta.color }} />
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2 text-center">
        <div>
          <div className="text-[13px] font-semibold text-[#1A2622] tabular-nums">{fmtInt(matched)}</div>
          <div className="text-[9.5px] uppercase tracking-wider text-[#7A857F]">Matched</div>
        </div>
        <div>
          <div className="text-[13px] font-semibold text-[#A06628] tabular-nums">{fmtInt(notFound)}</div>
          <div className="text-[9.5px] uppercase tracking-wider text-[#7A857F]">Not found</div>
        </div>
        <div>
          <div className="text-[13px] font-semibold text-[#7A857F] tabular-nums">{fmtInt(total)}</div>
          <div className="text-[9.5px] uppercase tracking-wider text-[#7A857F]">In platform</div>
        </div>
      </div>
    </div>
  );
}

function TaxonomyBar({ taxonomies }) {
  if (!taxonomies || taxonomies.length === 0) {
    return (
      <div className="rounded-xl bg-white border border-dashed border-[#E5DCC4] p-6 text-center">
        <Database size={20} className="text-[#A8B0AA] mx-auto" />
        <div className="mt-2 text-[12.5px] text-[#7A857F]">No taxonomy data yet. Trigger a sync to populate.</div>
      </div>
    );
  }
  const max = Math.max(...taxonomies.map((t) => t.count));
  return (
    <div className="rounded-xl bg-white border border-[#E5DCC4] p-5" data-testid="taxonomy-distribution">
      <h3 className="font-display text-base font-semibold text-[#1A2622] mb-3">Top NPPES taxonomies</h3>
      <ul className="space-y-2">
        {taxonomies.map((t, i) => {
          const pct = (t.count / max) * 100;
          return (
            <li key={i} className="flex items-center gap-3">
              <span className="text-[12px] text-[#1A2622] w-[260px] shrink-0 truncate">{t.name}</span>
              <div className="flex-1 h-2.5 rounded-full bg-[#F5EFDD] overflow-hidden">
                <div className="h-full rounded-full bg-[#4A7C5E]" style={{ width: `${pct}%` }} />
              </div>
              <span className="text-[11.5px] font-semibold text-[#1A2622] tabular-nums w-12 text-right">{t.count}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default function EntityIntelligencePage() {
  const [dash, setDash] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = () => {
    setLoading(true);
    api.identifiersDashboard()
      .then((d) => { setDash(d); setLoading(false); })
      .catch(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const triggerSync = async () => {
    setSyncing(true);
    setMsg(null);
    try {
      await api.runRefresh('last_identifiers_ingest');
      setMsg('Sync started. NPPES enrichment runs in the background; refresh in a few minutes to see updated coverage.');
    } catch (e) {
      setMsg('Could not start sync. Please try again.');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-[1280px] mx-auto px-6 lg:px-8 pt-6 pb-12">
        <Breadcrumb items={[{ label: 'Home', to: '/' }, { label: 'Entity Intelligence' }]} />

        <div className="mt-5 flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-[#34538F]/30 bg-white">
              <Fingerprint size={12} className="text-[#34538F]" />
              <span className="text-[11px] font-semibold uppercase tracking-wider text-[#34538F]">Provider Identifier &amp; Entity Intelligence</span>
            </div>
            <h1 className="mt-3 font-display text-4xl md:text-5xl font-semibold text-[#1A2622] tracking-tight leading-tight">
              One identity, every angle.
            </h1>
            <p className="mt-3 text-[13.5px] text-[#3A4A44] max-w-2xl">
              Cross-walk every CMS-certified SNF, hospital, and SNF-active physician on the platform
              against the public NPPES NPI Registry. Surfaces Legal Business Names, DBAs, Organization NPIs,
              primary taxonomies, authorized officials, and practice addresses — additive only, never replacing
              existing CMS data.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <GlobalEntitySearch />
            <button
              onClick={triggerSync}
              disabled={syncing}
              className="inline-flex items-center gap-1.5 px-3.5 h-9 rounded-md bg-[#1A2622] text-[#FBF6EC] text-[12.5px] font-medium hover:bg-[#2D5F4F] disabled:opacity-60"
              data-testid="entity-sync-now-btn"
            >
              <RefreshCw size={13} className={syncing ? 'animate-spin' : ''} />
              {syncing ? 'Starting…' : 'Run NPPES sync'}
            </button>
          </div>
        </div>

        {msg && (
          <div className="mt-4 rounded-lg bg-[#E5F0EA] border border-[#4A7C5E]/40 px-4 py-2.5 text-[12.5px] text-[#2D5F4F]" data-testid="entity-sync-msg">
            {msg}
          </div>
        )}

        <div className="mt-5"><DataFreshnessStrip /></div>

        {loading && (
          <div className="mt-8 text-center text-[13px] text-[#7A857F]">Loading coverage…</div>
        )}

        {!loading && dash && (
          <>
            <section className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="entity-coverage-grid">
              <CoverageCard entityType="facility"  dash={dash} />
              <CoverageCard entityType="hospital"  dash={dash} />
              <CoverageCard entityType="physician" dash={dash} />
            </section>

            <section className="mt-6">
              <TaxonomyBar taxonomies={dash.top_taxonomies} />
            </section>

            <section className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="rounded-xl bg-white border border-[#E5DCC4] p-5">
                <div className="flex items-center gap-2 mb-2.5">
                  <ShieldCheck size={14} className="text-[#2D5F4F]" />
                  <h3 className="font-display text-base font-semibold text-[#1A2622]">What this layer adds</h3>
                </div>
                <ul className="text-[12.5px] text-[#3A4A44] space-y-1.5 list-disc pl-5">
                  <li><strong>Organization NPIs (NPI-2)</strong> for facilities &amp; hospitals to unlock claims joins downstream.</li>
                  <li><strong>Legal Business Name + DBA</strong> &mdash; surfaces parent-company structure CMS Provider Info hides.</li>
                  <li><strong>Primary taxonomy code + description</strong> &mdash; lets you filter SNF vs LTACH vs ICF/IID precisely.</li>
                  <li><strong>Authorized official + enumeration date</strong> &mdash; useful for BD outreach &amp; M&amp;A diligence.</li>
                  <li><strong>NPPES practice addresses</strong> with telephones, complementing CMS billing addresses.</li>
                </ul>
              </div>
              <div className="rounded-xl bg-white border border-[#E5DCC4] p-5">
                <div className="flex items-center gap-2 mb-2.5">
                  <Info size={14} className="text-[#34538F]" />
                  <h3 className="font-display text-base font-semibold text-[#1A2622]">Methodology</h3>
                </div>
                <p className="text-[12.5px] text-[#3A4A44] leading-relaxed">{dash.methodology}</p>
                <p className="mt-3 text-[11.5px] text-[#A8B0AA]"><strong>Source:</strong> {dash.source}. Public data only — no PHI.</p>
              </div>
            </section>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
